// Flow badges — the shared rendering of a row's `badge` payload (#984, Epic
// #956 phase 2b, design §5.16/§7).
//
// A row's badge is produced ENTIRELY server-side by `ops.list_row_badges`
// (migration 6141): the missing-item counts come from the completeness
// evaluator (`ops.missing_items`, 6132) and the ageing numbers from
// `ops.entity_status_age` + `ops.age_level` (6135). See `RowBadge` in
// `@/lib/mcp`. Nothing in this file evaluates a rule or compares a day count
// against a threshold — `age_level` arrives already classified, because two
// users looking at the same card must see the same colour.
//
// The ONE aggregation this file does is `countNeedsAttention`: how many of a
// GIVEN SET OF ROWS carry a badge that needs attention. That is a count of
// server-provided answers, not a re-derivation of them.
//
// ── The scope rule for tab badges ──────────────────────────────────────────
//
// #980's review settled the rule this file obeys: **a badge must be computed
// with the same filter and the same scope as the tab's own contents.** Three of
// the four surfaces (#984's Briefs, QA and Shipments) filter SERVER-SIDE from
// their pill row, so when a pill is active the fetched rows cover that pill's
// scope and nothing else. `<TabCountBadge>` therefore renders only when the
// caller can say the loaded rows cover that tab (`covered`), and every badge
// carries a title naming its scope. A badge that cannot be computed honestly is
// not rendered at all — it is never guessed, and never silently shows a number
// derived from a different scope.

import type { ReactNode } from "react";
import { AlertTriangle, Clock } from "lucide-react";

import { cn } from "@/lib/cn";
import { rowBadge, type RowBadge } from "@/lib/flow-badge";

/** Whole days in status, floored — "12d" for 12.9 days. */
export function daysLabel(badge: RowBadge): string | null {
  if (badge.days_in_status === null) return null;
  return `${Math.max(0, Math.floor(badge.days_in_status))}d`;
}

/**
 * Does this row want a human to look at it? Either the completeness evaluator
 * reported a missing item, or `ops.age_level` classified it past a threshold.
 *
 * A row with NO badge is not "fine" — it is unknown — but it is also not
 * actionable, so it does not count. Null badges are therefore excluded.
 */
export function needsAttention(badge: RowBadge | null | undefined): boolean {
  if (!badge) return false;
  return badge.missing_total > 0 || badge.age_level !== "ok";
}

/** How many of `rows` need attention, by the definition above. */
export function countNeedsAttention(rows: readonly unknown[]): number {
  let n = 0;
  for (const row of rows) {
    if (needsAttention(rowBadge(row))) n += 1;
  }
  return n;
}

const AGE_TONE: Record<RowBadge["age_level"], string> = {
  ok: "bg-muted text-muted-foreground",
  warn: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  critical: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
};

const AGE_WORD: Record<RowBadge["age_level"], string> = {
  ok: "within the expected window for this stage",
  warn: "ageing — past the warn threshold for this stage",
  critical: "stalled — past the critical threshold for this stage",
};

/**
 * The missing-item count. `required` missing items are the ones that block a
 * stage transition (the brief soft gate, 6132 §8), so they set the tone;
 * recommended-only shows as a neutral pill.
 */
export function MissingItemsBadge({
  badge,
  className,
}: {
  badge: RowBadge | null | undefined;
  className?: string;
}) {
  if (!badge || badge.missing_total <= 0) return null;
  const blocking = badge.missing_required > 0;
  const parts = [
    badge.missing_required > 0 ? `${badge.missing_required} required` : null,
    badge.missing_recommended > 0 ? `${badge.missing_recommended} recommended` : null,
  ].filter(Boolean);
  return (
    <span
      data-flow-missing={badge.missing_total}
      title={`${badge.missing_total} missing checklist item${
        badge.missing_total === 1 ? "" : "s"
      } (${parts.join(", ")})`}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
        blocking
          ? "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200"
          : "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
        className,
      )}
    >
      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
      {badge.missing_total}
      <span className="sr-only">
        {` missing checklist item${badge.missing_total === 1 ? "" : "s"}`}
      </span>
    </span>
  );
}

/**
 * Days in the current status, toned by `ops.age_level`. Rendered only once the
 * row has aged into warn/critical on surfaces that pass `onlyWhenAged` — an
 * "ok" age on every row is noise, not information.
 */
export function DaysInStatusBadge({
  badge,
  onlyWhenAged = true,
  className,
}: {
  badge: RowBadge | null | undefined;
  onlyWhenAged?: boolean;
  className?: string;
}) {
  if (!badge) return null;
  const label = daysLabel(badge);
  if (label === null) return null;
  if (onlyWhenAged && badge.age_level === "ok") return null;
  return (
    <span
      data-flow-age={badge.age_level}
      title={`${label} in ${badge.status ?? "this status"} — ${AGE_WORD[badge.age_level]}`}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums",
        AGE_TONE[badge.age_level],
        className,
      )}
    >
      <Clock className="h-3 w-3" aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * Both row badges together — the kanban-card / table-cell rendering. Returns
 * null when there is nothing to say, so a caller can drop the whole cell.
 */
export function RowFlowBadges({
  badge,
  onlyWhenAged = true,
  className,
}: {
  badge: RowBadge | null | undefined;
  onlyWhenAged?: boolean;
  className?: string;
}) {
  const missing = badge && badge.missing_total > 0;
  const aged = badge && badge.days_in_status !== null &&
    (!onlyWhenAged || badge.age_level !== "ok");
  if (!missing && !aged) return null;
  return (
    <span className={cn("inline-flex items-center gap-1", className)}>
      <MissingItemsBadge badge={badge} />
      <DaysInStatusBadge badge={badge} onlyWhenAged={onlyWhenAged} />
    </span>
  );
}

/** The table-cell form: the badges, or an em-dash so the column stays aligned. */
export function FlowCell({ row }: { row: unknown }) {
  const badge = rowBadge(row);
  const content = <RowFlowBadges badge={badge} />;
  if (!badge || !needsAttention(badge)) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return content;
}

/**
 * The count badge that rides a page tab / filter pill / section header.
 *
 * `covered` is the honesty gate described in this file's header: pass `false`
 * when the loaded rows do NOT cover this tab's scope (e.g. a server-side status
 * filter is narrowing them to a different tab) and nothing is rendered rather
 * than a number from the wrong scope. `scopeNote` goes into the title so a
 * reader can tell what a number is counting (notably "on this page" for a
 * cursor-paginated surface).
 */
export function TabCountBadge({
  count,
  covered = true,
  active = false,
  scopeNote,
  label,
}: {
  count: number;
  covered?: boolean;
  active?: boolean;
  scopeNote?: string;
  label?: string;
}) {
  if (!covered || count <= 0) return null;
  const what = label ?? "needing attention";
  return (
    <span
      data-flow-attention={count}
      title={`${count} ${what}${scopeNote ? ` (${scopeNote})` : ""} — a missing checklist item, or stalled in this status`}
      className={cn(
        "ml-1 inline-flex min-w-4 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums",
        active
          ? "bg-amber-200 text-amber-950 dark:bg-amber-900 dark:text-amber-100"
          : "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
      )}
    >
      {count}
      <span className="sr-only">{` ${what}`}</span>
    </span>
  );
}

/**
 * The header-level affordance for the surfaces that have no tab row to badge
 * (Orders' Filters card today). Same data, scoped to the rows actually loaded,
 * and it says so.
 */
export function AttentionSummary({
  rows,
  scopeNote = "on this page",
  children,
}: {
  rows: readonly unknown[];
  scopeNote?: string;
  children?: ReactNode;
}) {
  const count = countNeedsAttention(rows);
  if (count <= 0) return null;
  return (
    <span
      data-flow-attention-summary={count}
      title={`${count} row${count === 1 ? "" : "s"} with a missing checklist item or stalled in status (${scopeNote})`}
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200"
    >
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="tabular-nums">{count}</span>
      {children ?? <span>need attention</span>}
    </span>
  );
}

/**
 * Per-bucket attention counts over a set of loaded rows.
 *
 * `bucketOf` says which tab a row belongs to. The returned map is keyed by
 * bucket, plus `ALL_BUCKET` for the whole set (the "All" pill). Rows whose
 * bucket is null are counted in `ALL_BUCKET` only.
 */
export const ALL_BUCKET = "";

export function attentionByBucket<T>(
  rows: readonly T[],
  bucketOf: (row: T) => string | null,
): Map<string, number> {
  const counts = new Map<string, number>();
  let all = 0;
  for (const row of rows) {
    if (!needsAttention(rowBadge(row))) continue;
    all += 1;
    const bucket = bucketOf(row);
    if (bucket === null) continue;
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  counts.set(ALL_BUCKET, all);
  return counts;
}
